/**
 * ReferralApiClient
 * 
 * Handles referral system functionality including tokens, rewards, and milestones
 */

import { BaseApiClient } from './BaseApiClient';

export interface ReferralToken {
  id: string;
  tokenKey: string;
  amount: number;
  currency: string;
  rewardAmount: number;
  status: 'active' | 'redeemed' | 'expired';
  isRedeemed: boolean;
  redeemedBy?: string;
  redeemedAt?: string;
  isNewUserReferral: boolean;
  rewardTriggered: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReferralReward {
  id: string;
  referralTokenId: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  processedAt?: string;
}

export interface ReferralMilestone {
  milestoneId: string;
  userId: string;
  milestoneLevel: string;
  targetValue: number;
  achievedValue: number;
  rewardOptions: Array<{
    currency: string;
    amount: number;
  }>;
  selectedReward?: {
    currency: string;
    amount: number;
  };
  status: 'achieved' | 'reward_given';
  achievedAt: string;
  expiresAt?: string;
}

export interface ReferralDashboard {
  stats: {
    tokensCreated: number;
    tokensRedeemed: number;
    totalRewards: number;
    successfulReferrals: number;
    pendingRewards: number;
  };
  recentActivity: Array<{
    type: 'token_created' | 'token_redeemed' | 'reward_earned';
    timestamp: string;
    details: any;
  }>;
  milestones: ReferralMilestone[];
}

export class ReferralApiClient extends BaseApiClient {
  /**
   * Get referral dashboard data
   */
  async getReferralDashboard(): Promise<ReferralDashboard> {
    return this.get('/user/referral/dashboard');
  }

  /**
   * Get user's referral tokens with pagination
   */
  async getReferralTokens(params?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'active' | 'redeemed' | 'expired';
  }): Promise<{
    tokens: ReferralToken[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);

    const qs = searchParams.toString();
    return this.get(qs ? `/user/referral/tokens?${qs}` : '/user/referral/tokens');
  }

  /**
   * Create a referral token
   */
  async createReferralToken(tokenData: {
    amount: number;
    currency: string;
    message?: string;
  }): Promise<{
    key: string;
    tokenObj: any;
    referralInfo: {
      isReferralToken: boolean;
      rewardPotential: number;
      message: string;
    };
  }> {
    return this.post('/user/referral/tokens', tokenData);
  }

  /**
   * Get referral rewards history
   */
  async getReferralRewards(params?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'pending' | 'completed' | 'failed';
  }): Promise<{
    rewards: ReferralReward[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    
    return this.get(`/referrals/rewards?${searchParams.toString()}`);
  }

  /**
   * Get user's referral milestones
   */
  async getReferralMilestones(): Promise<{
    milestones: ReferralMilestone[];
    currentProgress: {
      totalReferrals: number;
      nextMilestone?: {
        level: string;
        target: number;
        remaining: number;
        rewardOptions: Array<{
          currency: string;
          amount: number;
        }>;
      };
    };
  }> {
    return this.get('/user/referral/milestones');
  }

  /**
   * Claim milestone reward
   */
  async claimMilestoneReward(milestoneId: string, rewardChoice: {
    currency: string;
    amount: number;
  }): Promise<{
    success: boolean;
    message: string;
    rewardToken?: {
      key: string;
      amount: number;
      currency: string;
    };
  }> {
    return this.post(`/referrals/milestones/${milestoneId}/claim`, {
      rewardChoice
    });
  }

  /**
   * Check if user can create more referral tokens
   */
  async checkReferralTokenLimit(): Promise<{
    canCreate: boolean;
    tokensCreated: number;
    maxTokens: number;
    remaining: number;
  }> {
    return this.get('/user/referral/token-limit');
  }

  /**
   * Get referral statistics
   */
  async getReferralStats(period?: 'week' | 'month' | 'year' | 'all'): Promise<{
    totalTokensCreated: number;
    totalTokensRedeemed: number;
    totalRewardsEarned: number;
    successRate: number;
    averageTokenValue: number;
    topPerformingTokens: Array<{
      tokenKey: string;
      amount: number;
      currency: string;
      redemptionCount: number;
      totalRewards: number;
    }>;
    periodStats?: {
      period: string;
      tokensCreated: number;
      tokensRedeemed: number;
      rewardsEarned: number;
    };
  }> {
    const params = period ? `?period=${period}` : '';
    return this.get(`/referrals/stats${params}`);
  }

  /**
   * Get referral token details
   */
  async getReferralTokenDetails(tokenId: string): Promise<{
    token: ReferralToken;
    redemptions: Array<{
      redeemedBy: string;
      redeemedAt: string;
      isNewUser: boolean;
      rewardGiven: boolean;
    }>;
    rewards: ReferralReward[];
  }> {
    return this.get(`/referrals/tokens/${tokenId}/details`);
  }

  /**
   * Cancel referral token (if not redeemed)
   */
  async cancelReferralToken(tokenId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.post(`/referrals/tokens/${tokenId}/cancel`);
  }
} 